import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { chatApi, documentApi, authApi } from "@/services/api/endpoints";
import { login as authLogin, register as authRegister } from "@/services/auth";
import { STORAGE_KEYS } from "@/constants/index";
import { jwtDecode } from "jwt-decode";
import { logger } from "@/utils/logger";

// User type definition for LICITARS
interface User {
  id: string | number;
  username: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  role: "master" | "admin" | "gestor" | "operador"; // Role for access control
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: any | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  register: (userData: any) => Promise<void>;
}

interface JWTPayload {
  exp: number;
  user_id: number;
  token_type: string;
  iat: number;
  jti: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

// Token refresh cooldown in milliseconds (5 minutes)
const TOKEN_REFRESH_COOLDOWN = 5 * 60 * 1000;

/**
 * Ensures user has a valid role, defaulting to "operador" if not provided
 * TODO: This is a temporary fix. The backend should always return a role for the user.
 */
function ensureUserRole(user: any): User {
  return {
    ...user,
    role: (user as any).role || "operador"
  };
}

function validateToken(token: string): boolean {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const isValid = decoded.exp ? decoded.exp > currentTime : false;
    return isValid;
  } catch (error) {
    logger.error("[AuthContext] Erro ao validar token:", error);
    return false;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (storedToken && validateToken(storedToken)) {
      return storedToken;
    }
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    return null;
  });
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Refs for token refresh control
  const lastRefreshTime = useRef<number>(0);
  const refreshPromise = useRef<Promise<void> | null>(null);
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const response = await authLogin(username, password);

        const { access, refresh, user } = response;

        if (!validateToken(access)) {
          throw new Error("Invalid token received from server");
        }

        const userWithRole = ensureUserRole(user);

        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userWithRole));
        setToken(access);
        setUser(userWithRole);
        setIsAuthenticated(true);
        lastRefreshTime.current = Date.now();
        navigate("/");
      } catch (error) {
        console.error("[AuthContext] Login error:", error);
        toast({
          title: "Erro",
          description: "Credenciais inválidas. Por favor, tente novamente.",
          variant: "destructive",
        });
        throw error;
      }
    },
    [navigate, toast],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_DOCUMENT);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_PROCESS);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    lastRefreshTime.current = 0;
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
      refreshTimeout.current = null;
    }
    navigate("/login");
  }, [navigate]);

  const register = useCallback(
    async (userData: any) => {
      try {
        const registerResponse = await authRegister(userData.email, userData.password, userData.name);
        
        const { access: registerAccess, refresh: registerRefresh, user: registerUser } = registerResponse;

        if (!validateToken(registerAccess)) {
          throw new Error("Invalid token received from server");
        }

        const registerUserWithRole = ensureUserRole(registerUser);

        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, registerAccess);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, registerRefresh);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(registerUserWithRole));
        setToken(registerAccess);
        setUser(registerUserWithRole);
        setIsAuthenticated(true);
        lastRefreshTime.current = Date.now();
        navigate("/");
        
        toast({
          title: "Sucesso",
          description: "Usuário registrado com sucesso!",
        });
      } catch (error) {
        console.error("[AuthContext] Register error:", error);
        toast({
          title: "Erro",
          description: "Erro ao registrar usuário. Por favor, tente novamente.",
          variant: "destructive",
        });
        throw error;
      }
    },
    [navigate, toast],
  );

  const refreshToken = useCallback(async () => {
    const now = Date.now();

    // If we're within the cooldown period, return the existing promise or null
    if (now - lastRefreshTime.current < TOKEN_REFRESH_COOLDOWN) {
      if (refreshPromise.current) {
        return refreshPromise.current;
      }
      return;
    }

    // If there's already a refresh in progress, return that promise
    if (refreshPromise.current) {
      return refreshPromise.current;
    }

    try {
      // Create new refresh promise
      refreshPromise.current = (async () => {
        const refreshTokenValue = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshTokenValue) {
          throw new Error("No refresh token available");
        }
        
        const response = await authApi.refreshToken(refreshTokenValue);

        const { access } = response;

        if (!validateToken(access)) {
          throw new Error("Invalid token received from refresh");
        }

        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
        setToken(access);
        setIsAuthenticated(true);
        lastRefreshTime.current = Date.now();
      })();

      await refreshPromise.current;
    } catch (error) {
      console.error("[AuthContext] Token refresh error:", error);
      logout();
      throw error;
    } finally {
      refreshPromise.current = null;
    }
  }, [logout]);

  // Schedule next token refresh
  const scheduleNextRefresh = useCallback(() => {
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }

    const timeSinceLastRefresh = Date.now() - lastRefreshTime.current;
    const timeUntilNextRefresh = Math.max(
      0,
      TOKEN_REFRESH_COOLDOWN - timeSinceLastRefresh,
    );

    refreshTimeout.current = setTimeout(() => {
      refreshToken().catch((error) => {
        console.error("[AuthContext] Scheduled token refresh failed:", error);
      });
    }, timeUntilNextRefresh);
  }, [refreshToken]);

  // Initial token check and refresh scheduling
  useEffect(() => {
    let isMounted = true;
    const initializeAuth = () => {
      const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (storedToken && validateToken(storedToken)) {
        if (isMounted) {
          setToken(storedToken);
          setIsAuthenticated(true);
          // Restore user data from localStorage
          if (storedUserData) {
            try {
              const parsedUser = JSON.parse(storedUserData);
              setUser(parsedUser);
            } catch (error) {
              console.error("[AuthContext] Error parsing stored user data:", error);
              // If user data is corrupted, clear it but keep the valid token
              localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            }
          }
          lastRefreshTime.current = Date.now();
          scheduleNextRefresh();
        }
      } else {
        // Token invalid or missing, clear everything
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        if (isMounted) {
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      if (isMounted) {
        setIsLoading(false);
      }
    };
    initializeAuth();
    return () => {
      isMounted = false;
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, [scheduleNextRefresh]);

  // Schedule next refresh after successful token refresh
  useEffect(() => {
    if (token) {
      scheduleNextRefresh();
    }
  }, [token, scheduleNextRefresh]);

  const memoizedValue = React.useMemo(
    () => ({
      isAuthenticated,
      token,
      user,
      isLoading,
      login,
      logout,
      refreshToken,
      register,
    }),
    [
      isAuthenticated,
      token,
      user,
      isLoading,
      login,
      logout,
      refreshToken,
      register,
    ],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
